import React, { useEffect, useState } from 'react'
import { LexicalTypeaheadMenuPlugin, MenuOption } from '@lexical/react/LexicalTypeaheadMenuPlugin'
import type { Profile } from '../../user/Profile'
import { $nodesOfType, type TextNode } from 'lexical'
import { fuzzyFilter } from '../../../../shared/domain/schedule/assignee.no-deps'
import { $createMentionNode, MentionNode } from '../nodes/MentionNode'
import type { SupportedLanguage } from '../../../../shared/i18n/types.no-deps'
import { UserList } from '../../../../shared/webcomponents/react-wrappers/UserList'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'

type Props = {
    profiles: Profile[]
    language: SupportedLanguage
    disabled: boolean
    below?: boolean
    onMentionsChange?: (nodes: MentionNode[]) => void
}

export function MentionsPlugin({ profiles, language, disabled, below, onMentionsChange }: Props) {
    const [query, setQuery] = useState<string | null>(null)
    const [editor] = useLexicalComposerContext()

    useEffect(() => {
        if (!onMentionsChange) {
            return
        }

        editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const currentMentionNodes = $nodesOfType(MentionNode)
                onMentionsChange(currentMentionNodes)
            })
        })
    }, [editor])

    const filteredProfiles = filterProfiles(profiles, query)
    const options = filteredProfiles.map((p) => new ProfileOption(p))

    return (
        <LexicalTypeaheadMenuPlugin
            onQueryChange={setQuery}
            onSelectOption={onSelect}
            options={options}
            menuRenderFn={(_anchorElementRef, { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex }) => (
                <ProfileMenu
                    profiles={filteredProfiles}
                    selected={selectedIndex}
                    onSelect={(index: number) => selectOptionAndCleanUp(options[index])}
                    onHover={setHighlightedIndex}
                    language={language}
                    below={below}
                />
            )}
            triggerFn={disabled ? () => null : findMention}
        />
    )
}

function filterProfiles(profiles: Profile[], query: string | null) {
    if (query === null) {
        return []
    }

    const usableProfiles = profiles.filter((p) => p.name)
    const filtered = fuzzyFilter(usableProfiles, query)
    if (filtered.length === 0) {
        return usableProfiles
    }

    return filtered
}

function ProfileMenu({
    profiles,
    selected,
    below,
    onSelect,
    onHover,
    language,
}: {
    profiles: Profile[]
    selected: number
    below?: boolean
    onSelect: (index: number) => void
    onHover: (index: number) => void
    language: SupportedLanguage
}) {
    if (profiles.length === 0) {
        return <></>
    }

    return (
        <zaptic-popover class="AppCore__lexical__MentionsPlugin_popover" {...(below ? {} : { above: true })}>
            <UserList
                items={profiles}
                selected={selected}
                language={language}
                onSelected={(event) => onSelect(event.detail)}
                onHovered={(event) => onHover(event.detail)}
            />
        </zaptic-popover>
    )
}

function findMention(text: string) {
    const regex = /(^| )(@)([\p{L}\p{N}]*)$/u
    const match = text.match(regex)
    if (match === null) {
        return null
    }

    const query = match[3] || ''
    return {
        leadOffset: match.index,
        matchingString: query,
        replaceableString: match[2] + match[3],
    }
}

function onSelect(option: ProfileOption, node: TextNode | null, close: () => void) {
    if (node) {
        node.replace($createMentionNode(option.profile.userId))
    }
    close()
}

class ProfileOption extends MenuOption {
    profile: Profile

    constructor(profile: Profile) {
        super(profile.name)
        this.profile = profile
    }
}
